import { ReactNode, createContext, useContext, useState } from 'react';
import { SUBSCRIPTION_BILLING_CYCLES, SUBSCRIPTION_LEVELS } from '@sdk/account/constants';

import { BillingAddress } from '@web/account/types';
import { useTaxCalculations } from '@web/account/hooks/useTaxCalculations';
import { getIsYearlyPlan } from '@web/account/utils';

import { PurchaseContextType } from './purchaseProvider.types';

export const PurchaseContext = createContext<PurchaseContextType>({} as PurchaseContextType);

type Props = {
  initialLevel: number;
  isOldPremium: boolean;
  children: ReactNode;
  initialBillingCycle: SUBSCRIPTION_BILLING_CYCLES;
};

export const usePurchaseContext = () => useContext(PurchaseContext);

export const PurchaseContextProvider = ({
  initialLevel,
  isOldPremium,
  children,
  initialBillingCycle = SUBSCRIPTION_BILLING_CYCLES.MONTHLY,
}: Props) => {
  const [billingAddress, setBillingAddress] = useState<BillingAddress | null>(null);
  const [subscriptionLevel, setSubscriptionLevel] = useState(
    !isOldPremium ? initialLevel || SUBSCRIPTION_LEVELS.PREMIUM : SUBSCRIPTION_LEVELS.OLD_PREMIUM
  );
  const [subscriptionBillingCycle, setSubscriptionBillingCycle] = useState(initialBillingCycle);
  const [monthlyPricePerUserPerMonth, setMonthlyPricePerUserPerMonth] = useState(0);
  const { taxPercent } = useTaxCalculations(
    billingAddress,
    subscriptionLevel,
    getIsYearlyPlan(subscriptionBillingCycle)
  );

  const contextValue: PurchaseContextType = {
    billingAddress,
    monthlyPricePerUserPerMonth,
    setBillingAddress: (address: BillingAddress) => setBillingAddress(address),
    setMonthlyPricePerUserPerMonth,
    subscriptionLevel,
    setSubscriptionLevel,
    subscriptionBillingCycle,
    setSubscriptionBillingCycle,
    taxPercent,
  };

  return <PurchaseContext.Provider value={contextValue}>{children}</PurchaseContext.Provider>;
};
